import type { IDBPDatabase } from 'idb'
import { STORE } from './schema'

/** Every record in the database, keyed by store name. */
export type StoreDump = Readonly<Record<string, readonly unknown[]>>

/**
 * A connection read without reference to the compiled schema.
 *
 * `idb`'s generic `IDBPDatabase<SolaDB>` and `IDBPDatabase<unknown>` are not
 * assignable to each other — the schema parameter appears in both input and
 * output positions, so the types are invariant. The functions here must work
 * against ANY version of the database, including one written by an older build
 * during recovery, so they take the untyped form.
 *
 * This is the single conversion point, named so the reason travels with it
 * rather than being an unexplained cast at each call site.
 */
export type SchemaAgnosticDatabase = IDBPDatabase<unknown>

export function asSchemaAgnostic(db: object): SchemaAgnosticDatabase {
  return db as SchemaAgnosticDatabase
}

export interface ReadAllOptions {
  /** Blob payloads are excluded from the pre-migration snapshot (D-029). */
  readonly includeBlobs?: boolean
}

/**
 * Read every store, schema-agnostically.
 *
 * Deliberately enumerates `objectStoreNames` rather than referencing the
 * compiled schema, so it works against ANY prior database version. Recovery
 * mode depends on that: after a failed upgrade the app code is newer than the
 * data it must read.
 *
 * Uses one short read-only transaction per store, with no awaits spanning a
 * transaction boundary. A single transaction covering all stores would be held
 * open across every `getAll`, and any incidental async work would commit it.
 */
export async function readAllStores(
  db: IDBPDatabase,
  options: ReadAllOptions = {},
): Promise<StoreDump> {
  const includeBlobs = options.includeBlobs ?? false
  const names = Array.from(db.objectStoreNames)
  const dump: Record<string, readonly unknown[]> = {}

  for (const name of names) {
    if (!includeBlobs && name === STORE.blobs) continue
    // Each iteration opens and completes its own transaction.
    dump[name] = await db.getAll(name)
  }

  return dump
}

/** Record counts per store, for verification and for recovery-mode display. */
export async function countAllStores(db: IDBPDatabase): Promise<Readonly<Record<string, number>>> {
  const counts: Record<string, number> = {}
  for (const name of Array.from(db.objectStoreNames)) {
    counts[name] = await db.count(name)
  }
  return counts
}
